import { useState, useEffect } from 'react';
import { notification } from 'antd';
import { DigitalTwin } from 'types/graphql-apollo-v2';
import {
  LogicalRule,
  logicalRuleToFilter,
  parseFilterToLogicalRule,
} from 'utils/rules';
import { defaultLogicalRule } from 'utils/nested-conditions';
import { useSchemaData } from '../useSchemaData';
import { ConsistencySchemaFilterView } from '../types';

const MAX_STEP = 4;

type UseEditSchemaFilterDrawerParams = {
  schemaFilter: ConsistencySchemaFilterView | undefined;
  visible: boolean;
  onClose: VoidFunction;
  onSchemaFilterUpdated: (
    id: string,
    params: {
      name: string;
      description: string;
      labels: string[];
      schemaType: string;
      schemaCollectionId: string;
      filters: string;
    }
  ) => Promise<void>;
  digitalTwin: DigitalTwin;
};

export const useEditSchemaFilterDrawer = ({
  schemaFilter,
  visible,
  onClose,
  onSchemaFilterUpdated,
  digitalTwin,
}: UseEditSchemaFilterDrawerParams) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [labelListInput, setLabelListInput] = useState<string[]>([]);
  const [selectedSchemaName, setSelectedSchemaName] = useState('');
  const [selectedSchemaType, setSelectedSchemaType] = useState('');
  const [filtersTree, setFiltersTree] =
    useState<LogicalRule>(defaultLogicalRule);

  const { schemas, loading: loadingSchemas } = useSchemaData(digitalTwin);

  const selectedSchema = schemas.find(
    (s) => s.schemaCollection.name === selectedSchemaName
  );

  const hasInvalidName = !nameInput.trim();
  const hasInvalidSchema = !selectedSchemaName;
  const hasInvalidSchemaType = !selectedSchemaType;
  const hasInvalidValues =
    hasInvalidName || hasInvalidSchema || hasInvalidSchemaType;

  useEffect(() => {
    if (visible && schemaFilter) {
      setNameInput(schemaFilter.name);
      setDescriptionInput(schemaFilter.description ?? '');
      setLabelListInput(schemaFilter.labels ?? []);
      setSelectedSchemaName(schemaFilter.schemaCollectionName);
      setSelectedSchemaType(schemaFilter.schemaType);
      setFiltersTree(
        schemaFilter.filters
          ? parseFilterToLogicalRule(schemaFilter.filters)
          : defaultLogicalRule
      );
      setCurrentStep(0);
    }
  }, [visible, schemaFilter]);

  const nextStep = () => {
    setCurrentStep((prev) => Math.min(prev + 1, MAX_STEP));
  };

  const handleSchemaChange = (schemaName: string) => {
    setSelectedSchemaName(schemaName);
    setSelectedSchemaType('');
  };

  const handleSubmit = async () => {
    if (creating || hasInvalidValues || !schemaFilter) return;
    setCreating(true);

    try {
      await onSchemaFilterUpdated(schemaFilter.externalId, {
        name: nameInput.trim(),
        description: descriptionInput,
        labels: labelListInput,
        schemaType: selectedSchemaType,
        schemaCollectionId:
          selectedSchema?.schemaCollection.externalId ??
          schemaFilter.schemaCollectionId,
        filters: JSON.stringify(
          logicalRuleToFilter(
            filtersTree,
            selectedSchema?.types?.find((t) => t.name === selectedSchemaType)
              ?.properties ?? []
          )
        ),
      });

      notification.success({
        message: 'Schema filter updated',
        description: `Successfully updated "${nameInput.trim()}"`,
      });

      onClose();
    } catch (err: unknown) {
      notification.error({
        message: 'Could not update schema filter',
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setCreating(false);
    }
  };

  return {
    creating,
    nameInput,
    setNameInput,
    descriptionInput,
    setDescriptionInput,
    labelListInput,
    setLabelListInput,
    selectedSchemaName,
    selectedSchemaType,
    setSelectedSchemaType,
    filtersTree,
    setFiltersTree,
    schemas,
    loadingSchemas,
    selectedSchema,
    hasInvalidName,
    hasInvalidSchema,
    hasInvalidSchemaType,
    hasInvalidValues,
    nextStep,
    handleSchemaChange,
    currentStep,
    setCurrentStep,
    handleSubmit,
  };
};
